// Shared placement logic for objects added via the side tools (upload/clipart/artwork).
// Canvas dimensions are the admin-configured design-area rectangle, which is often much
// smaller than a freshly uploaded photo — so every added object must be scaled to fit
// inside it and centered, never dropped at a hardcoded pixel offset that could land
// outside the visible/clipped canvas bounds.

const FIT_RATIO = 0.85; // leave a margin so the object doesn't touch the edges — matches TextTool's TEXT_FIT_RATIO so every tool defaults to the same relative size

// This app implements zoom by physically resizing the Fabric canvas's own backing
// store (`canvas.width`/`height` ARE the zoomed pixel dimensions — see
// useFabricCanvas.ts/DesignerWorkspace.tsx), not by leaving the canvas at its
// logical size and scaling a viewportTransform translation. That makes `canvas.width`/
// `height` the WRONG values to size or center new objects against whenever zoom != 1:
// object `left`/`top`/`width` are logical units that get multiplied by zoom again at
// render time, so sizing/centering against the already-zoomed canvas dimensions
// double-applies the zoom factor — confirmed live (real bug, not cosmetic): at a
// representative 0.767 zoom, text/images rendered at roughly 77% of their intended
// size, offset toward the canvas's top-left corner, looking "not centered" exactly
// as reported. `canvas.centerObject()` itself is also unsafe here for the same
// reason — it centers using `getCenterPoint()` (raw zoomed-pixel center), not the
// zoom-aware `getVpCenter()`. `_originalWidth`/`_originalHeight` (set once in
// useFabricCanvas.ts, alongside the real per-side design-area size) are the logical,
// zoom-independent dimensions that should be used instead.
export function getLogicalSize(canvas: any): { width: number; height: number } {
  const zoom = typeof canvas.getZoom === 'function' ? canvas.getZoom() : 1;
  const width  = canvas._originalWidth  ?? (canvas.width  ?? 400) / (zoom || 1);
  const height = canvas._originalHeight ?? (canvas.height ?? 400) / (zoom || 1);
  return { width, height };
}

export function centerObjectLogical(canvas: any, obj: any) {
  const { width, height } = getLogicalSize(canvas);
  obj.set({
    left: width  / 2,
    top:  height / 2,
    originX: 'center',
    originY: 'center',
  });
  obj.setCoords();
}

// Some clipart SVGs (a legacy CorelDRAW export batch — confirmed via `storage/clipart/*.svg`:
// ~1,178 of 10,816 files declare `width="8.5in" height="11in"` with a `viewBox="0 0 8500 11000"`)
// carry the artwork as a small shape positioned off-center inside a full letter-page canvas,
// instead of a tight bounding box around just the artwork. `fabric.Image.fromURL` reports
// `img.width`/`img.height` as that full page (816x1056px @96dpi), so `addImageFitted` fit/centers
// the WHOLE mostly-transparent page — the visible artwork then renders small and off-center
// inside the design area (confirmed live: the same "cropped" symptom the ResizeObserver fix
// didn't touch, reproduced with a real "air-force-14.svg" clipart tile against a mug design area).
// This trims the image to its actual non-transparent content first, so centering/fitting acts on
// the artwork itself — same pixel-scan bounding-box technique as `fabric.curvedText.ts`'s
// `_trimCanvas`, but expressed as a Fabric `cropX`/`cropY`/width/height crop (keeps the original
// element as the image source) rather than physically resizing a canvas.
function trimTransparentBorder(img: any) {
  const el = img.getElement ? img.getElement() : img._element;
  const w = img.width, h = img.height;
  if (!el || !w || !h) return;
  const scratch = document.createElement('canvas');
  scratch.width = w; scratch.height = h;
  const ctx = scratch.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(el, 0, 0, w, h);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return; // tainted canvas (cross-origin without CORS headers) — skip, fit the full image as before
  }
  let minX = w, maxX = 0, minY = h, maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[((y * w + x) * 4) + 3] > 0) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return; // fully transparent image — nothing to trim
  const trimmedW = maxX - minX + 1, trimmedH = maxY - minY + 1;
  if (trimmedW === w && trimmedH === h) return; // already tight — no-op, skip the extra render
  img.set({ cropX: minX, cropY: minY, width: trimmedW, height: trimmedH });
}

export function addImageFitted(canvas: any, img: any) {
  trimTransparentBorder(img);
  const { width: cw, height: ch } = getLogicalSize(canvas);
  const iw = img.width  ?? 1;
  const ih = img.height ?? 1;

  // Fit within the placeholder (capped by height per product requirement, but also
  // capped by width so a very wide image doesn't overflow horizontally). Never upscale.
  const scale = Math.min((ch * FIT_RATIO) / ih, (cw * FIT_RATIO) / iw, 1);
  img.scale(scale);

  canvas.add(img);
  centerObjectLogical(canvas, img);
  canvas.setActiveObject(img);
  canvas.renderAll();
}

// The scaleX/scaleY an already-placed image object needs AFTER its own pixel
// content is swapped out for a different-sized image (Fabric's `setSrc()`),
// so its RENDERED FOOTPRINT on canvas — `width*scaleX` × `height*scaleY` —
// stays exactly what it was before the swap.
//
// REGRESSION (found 2026-09-11, 10th Design Guide PDF verification loop
// extension): BackgroundColorTool.tsx's "Correct Colors" wizard swaps an
// existing object's image in place via `existingObject.setSrc(previewUrl,
// callback)` whenever the Crop/Rotate step bakes a crop into a genuinely
// SMALLER-pixel-dimension PNG (its own `applyCropRotate` sets
// `outCanvas.width/height` to just the surviving cropped region — no
// compensating scale computed anywhere). Fabric 5.5.2's own `setSrc()`
// (fabric.js:21258) always calls `_setWidthHeight()` (fabric.js:21490),
// which overwrites `this.width`/`this.height` from the NEW element's
// `naturalWidth`/`naturalHeight` but never touches `scaleX`/`scaleY` — so
// the object's rendered size silently shrinks by exactly the crop ratio.
// Confirmed via direct reproduction with real Fabric.js and real image
// files: a 2000px image scaled to a 100-canvas-unit footprint, cropped to
// 1000px, rendered footprint silently halved to 50 units with scaleX/scaleY
// completely unchanged. Since `printSpecs.ts`'s own `widthMm`/`heightMm` is
// computed from this same `width*scaleX`, an uncorrected crop would also
// make the Design Guide PDF report a physically wrong (too-small) artwork
// size for the exact object a customer just edited.
//
// Call this AFTER `setSrc()`'s callback fires (once `img.width`/`img.height`
// already reflect the NEW image), passing the footprint captured from the
// object's OWN width/height/scaleX/scaleY BEFORE calling `setSrc()`.
export function scaleToPreserveFootprint(
  img: { width: number; height: number; scaleX: number; scaleY: number },
  priorFootprintWidth: number,
  priorFootprintHeight: number,
): { scaleX: number; scaleY: number } {
  return {
    // Falls back to the object's own current scale (not 0/NaN/Infinity) if
    // the new image somehow reports a zero width/height — a genuine edge
    // case (a failed/corrupt load) this function must not crash or produce
    // an invalid canvas transform for, confirmed via direct reproduction.
    scaleX: img.width  > 0 ? priorFootprintWidth  / img.width  : img.scaleX,
    scaleY: img.height > 0 ? priorFootprintHeight / img.height : img.scaleY,
  };
}
